import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import { ISocialsBlockData } from "../../../types/content-block-data";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import SatoriButton from "../shared/satori-ui/button";
import { SquareSquare } from "lucide-react";
import { IconType } from "react-icons";
import {
  SiFacebook,
  SiX, // X (formerly Twitter)
  SiInstagram,
  SiLinkedin,
  SiYoutube,
  SiTiktok,
  SiWhatsapp,
  SiTelegram,
  SiPinterest,
  SiSnapchat,
  SiReddit,
  SiGithub,
  SiDribbble,
  SiBehance,
  SiMedium,
  SiSlack,
  SiDiscord,
  SiVimeo,
  SiTumblr,
  SiTwitch,
  SiLine,
  SiSpotify,
  SiSoundcloud,
  SiGoogle,
  SiApple,
  SiMessenger,
  SiMaildotru, // For email (can use SiGmail for Gmail as well)
} from "react-icons/si";
import { Circle, Square, PlusCircle, Trash2 } from "lucide-react";
import { useState, Fragment } from "react";
import SelectedSocialLink from "../shared/socials-sharing/selected-social-link";
import SocialLinkDropZone from "../shared/socials-sharing/social-link-drop-zone";
import SatoriTextBox from "../shared/satori-ui/textbox";
import { ALIGN_OPTIONS, NOPERCENT_UNITS } from "../../../types/constants";

/** Shape options for icons */
const ICON_SHAPES = [
  { label: "Circle", value: "circle", icon: <Circle size={16} /> },
  { label: "Square", value: "square", icon: <Square size={16} /> },
];

/** Supported social platforms */
const SOCIALS: {
  type: string;
  label: string;
  icon: IconType;
  color: string;
}[] = [
  { type: "facebook", label: "Facebook", icon: SiFacebook, color: "#1877F2" },
  { type: "twitter", label: "X (Twitter)", icon: SiX, color: "#000000" },
  {
    type: "instagram",
    label: "Instagram",
    icon: SiInstagram,
    color: "#E1306C",
  },
  { type: "linkedin", label: "LinkedIn", icon: SiLinkedin, color: "#0077B5" },
  { type: "youtube", label: "YouTube", icon: SiYoutube, color: "#FF0000" },
  { type: "tiktok", label: "TikTok", icon: SiTiktok, color: "#000000" },
  { type: "whatsapp", label: "WhatsApp", icon: SiWhatsapp, color: "#25D366" },
  { type: "telegram", label: "Telegram", icon: SiTelegram, color: "#0088CC" },
  {
    type: "pinterest",
    label: "Pinterest",
    icon: SiPinterest,
    color: "#E60023",
  },
  { type: "snapchat", label: "Snapchat", icon: SiSnapchat, color: "#FFFC00" },
  { type: "reddit", label: "Reddit", icon: SiReddit, color: "#FF4500" },
  { type: "github", label: "GitHub", icon: SiGithub, color: "#333333" },
  { type: "dribbble", label: "Dribbble", icon: SiDribbble, color: "#EA4C89" },
  { type: "behance", label: "Behance", icon: SiBehance, color: "#1769FF" },
  { type: "medium", label: "Medium", icon: SiMedium, color: "#000000" },
  { type: "slack", label: "Slack", icon: SiSlack, color: "#611F69" },
  { type: "discord", label: "Discord", icon: SiDiscord, color: "#5865F2" },
  { type: "vimeo", label: "Vimeo", icon: SiVimeo, color: "#1AB7EA" },
  { type: "tumblr", label: "Tumblr", icon: SiTumblr, color: "#36465D" },
  { type: "twitch", label: "Twitch", icon: SiTwitch, color: "#9146FF" },
  { type: "line", label: "LINE", icon: SiLine, color: "#00C300" },
  { type: "spotify", label: "Spotify", icon: SiSpotify, color: "#1DB954" },
  {
    type: "soundcloud",
    label: "SoundCloud",
    icon: SiSoundcloud,
    color: "#FF5500",
  },
  { type: "google", label: "Google", icon: SiGoogle, color: "#4285F4" },
  { type: "apple", label: "Apple", icon: SiApple, color: "#000000" },
  {
    type: "messenger",
    label: "Messenger",
    icon: SiMessenger,
    color: "#0078FF",
  },
  { type: "mailru", label: "Email", icon: SiMaildotru, color: "#005FF9" },
];

/**
 * SocialsOptions – Sidebar Editing Panel for Social Sharing Blocks
 *
 * Provides a sidebar panel for configuring the icons, links, styles, and layout
 * of a Social Sharing block in the Satori PageBuilder. Supports selection, reordering,
 * and configuration of all common social platforms, as well as icon style, shape, size,
 * and spacing.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Social Sharing content block to edit.
 *
 * @example
 * // Render socials options panel for a given Social Sharing block
 * <SocialsOptions blockId={selectedBlockId} />
 *
 * @remarks
 * - Fetches and updates the target Social Sharing block's data from the MobX contentManager store.
 * - Renders all configurable fields: icon shape (circle/square), icon size, spacing, and the full social icon list.
 * - Allows adding, removing, and (future: drag & drop) reordering social links; auto-populates defaults for new platforms.
 * - Each icon’s URL can be individually configured. Supports all common platforms (Facebook, X/Twitter, LinkedIn, etc).
 * - All field changes call `contentManager.setSocialsConfig` for atomic and reactive MobX state updates.
 * - Uses the Satori UI kit for all controls for a consistent UX.
 * - Mirrors the options panel structure of ButtonOptions, ImageOptions, and ColumnsOptions for consistency.
 * - Fully reactive via MobX's `observer()` wrapper.
 *
 * @see contentManager.setSocialsConfig
 * @see ButtonOptions, ImageOptions, ColumnsOptions – For parallel sidebar editing patterns
 * @see SelectedSocialLink, SocialLinkDropZone – For drag/drop and icon editing patterns
 */
export default observer(function SocialsOptions({
  blockId,
}: {
  blockId: string;
}) {
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "socials") return null;
  const socialsData = block.blockData as ISocialsBlockData;

  const getSocialMeta = (type: string) =>
    SOCIALS.find((s) => s.type === type) || {};

  // Handler to update socials block fields (partial update)
  function update(field: keyof ISocialsBlockData, value: any) {
    contentManager.setSocialsConfig(blockId, { [field]: value });
  }

  // Handler for updating a specific social link entry
  function updateSocial(idx: number, field: string, value: string) {
    const updated = [...(socialsData.socials || [])];
    updated[idx] = { ...updated[idx], [field]: value };
    update("socials", updated);
  }

  function moveSocialLink(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    const updated = [...socialsData.socials];
    const [removed] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, removed);
    update("socials", updated);
  }

  // Handler for removing a social entry
  function removeSocial(idx: number) {
    const updated = [...(socialsData.socials || [])];
    updated.splice(idx, 1);
    update("socials", updated);
  }

  // Handler for adding a new entry (opens menu, etc)
  function addSocial(type: string) {
    const alreadyExists = socialsData.socials.some((s) => s.type === type);
    if (!alreadyExists) {
      const defaultUrl = SOCIALS.find((s) => s.type === type)?.label
        ? `https://${type}.com`
        : "#";
      update("socials", [
        ...(socialsData.socials || []),
        { type, url: defaultUrl },
      ]);
    }
  }

  // Handler for icon shape
  function handleShapeChange(shape: string) {
    update("iconShape", shape);
  }

  // --- UI ---
  return (
    <section className="flex flex-col">
      <div className="flex flex-col gap-3 p-4">
        {/* Icon Shape Selector */}
        <div className="flex gap-2 mb-2">
          <span className="font-medium">Shape:</span>
          {ICON_SHAPES.map((opt) => (
            <button
              key={opt.value}
              className={`flex items-center gap-1 px-2 py-1 rounded 
									${socialsData.iconShape === opt.value ? "bg-blue-100 border-blue-400" : "bg-gray-100"}
								`}
              onClick={() => handleShapeChange(opt.value)}
              type="button"
            >
              {opt.icon}
              {opt.label}
            </button>
          ))}
        </div>

        {/* Socials List (sortable in future) */}
        <div className="flex flex-col gap-2">
          {socialsData.socials.map((item, idx) => {
            const { icon: Icon, color } = getSocialMeta(item.type) as {
              icon: IconType | undefined;
              color: string | undefined;
            };
            return (
              <Fragment key={item.type + idx}>
                <SocialLinkDropZone dropIndex={idx} onDrop={moveSocialLink} />
                <SelectedSocialLink index={idx}>
                  <span
                    className="w-7 h-7 flex items-center justify-center rounded"
                    style={{ background: color || "#eee", width: "28px" }}
                  >
                    {Icon ? Icon({ size: 20, color: "#fff" }) : <span>?</span>}
                  </span>
                  <input
                    type="url"
                    value={item.url}
                    onChange={(e) => updateSocial(idx, "url", e.target.value)}
                    placeholder={`https://${item.type}.com/yourprofile`}
                    className="border-gray-300 border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-700"
                  />
                  <button
                    className="p-1 text-red-400 hover:text-red-600"
                    onClick={() => removeSocial(idx)}
                    type="button"
                    title="Remove"
                  >
                    <span className="sr-only">Remove</span>✕
                  </button>
                </SelectedSocialLink>
              </Fragment>
            );
          })}
          {/* Final drop zone after the last item */}
          <SocialLinkDropZone
            dropIndex={socialsData.socials.length}
            onDrop={moveSocialLink}
          />
        </div>

        {/* Add new social */}
        <div className="flex flex-wrap gap-2 mt-2">
          {SOCIALS.filter(
            (s) => !socialsData.socials.some((e) => e.type === s.type)
          ).map((soc) => {
            const Icon = soc.icon;
            return (
              <button
                key={soc.type}
                className="flex items-center gap-1 p-1 rounded bg-gray-100 hover:bg-blue-100"
                onClick={() => addSocial(soc.type)}
                type="button"
              >
                <span
                  className="w-6 h-6 flex items-center justify-center rounded"
                  style={{ background: soc.color }}
                >
                  {Icon ? Icon({ size: 20, color: "#fff" }) : <span>?</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <SatoriCollapsiblePanel
        icon={<SquareSquare size={18} strokeWidth={1.5} />}
        title={"Layout Options"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Alignment */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Alignment"}
            options={ALIGN_OPTIONS}
            isFontFamily={false}
            value={socialsData.alignment || "none"}
            onChange={(val) => update("alignment", val)}
          />
        </div>
      </SatoriCollapsiblePanel>
      {/* Icon size, padding, spacing, etc. */}
      <SatoriCollapsiblePanel title="Icon Style" defaultOpen={true}>
        <div className="flex flex-col gap-3">
          {/* Icon Size */}
          <SatoriDimensionsBox
            label="Icon Size"
            value={socialsData.iconSize?.toString() || "36"}
            unit="px"
            units={["px"]}
            onValueChange={(val) => update("iconSize", val)}
            onUnitChange={() => {}} // Locked to px
          />
          {/* Padding/spacing, add as needed */}
          <SatoriDimensionsBox
            label="Icon Spacing"
            value={socialsData.spacing?.toString() || "8"}
            unit="px"
            units={["px"]}
            onValueChange={(val) => update("spacing", val)}
            onUnitChange={() => {}}
          />
        </div>
        {/* Future: per-icon color, hover color, border, etc */}
      </SatoriCollapsiblePanel>
    </section>
  );
});
